<template>
  <div class="auth-form content fade-in">
    <h1>{{ isLogin ? $t('account.auth.login') : $t('account.auth.register') }}</h1>

    <div class="social" id="socialbuttons"></div>

    <hr style="width: 80px" />

    <form
      v-if="isLogin && loginMethod === 'otp'"
      @submit.prevent="otpStep === 'email' ? sendOtp() : verifyOtp()"
    >
      <label v-if="otpStep === 'email'">
        {{ $t('account.auth.yourEmail') }}
        <input type="email" v-model="otpEmail" autocomplete="email" required />
      </label>
      <p v-if="otpStep === 'email'" class="otp-hint muted">{{ $t('account.auth.otpNewAccountHint') }}</p>
      <template v-else>
        <p class="otp-hint" role="status">{{ $t('account.auth.otpSent', { email: otpEmail }) }}</p>
        <label>
          {{ $t('account.auth.otpCode') }}
          <input
            type="text"
            v-model.trim="otpCode"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="10"
            required
          />
        </label>
      </template>
      <p v-if="authError" class="auth-error" role="alert">{{ authError }}</p>
      <button class="btn-cta" type="submit" :disabled="busy">
        {{ otpStep === 'email' ? $t('account.auth.sendCode') : $t('account.auth.verifyCode') }}
      </button>
      <div v-if="otpStep === 'code'" class="otp-actions">
        <button class="link-button" type="button" :disabled="busy" @click="sendOtp">
          {{ $t('account.auth.resendCode') }}
        </button>
        <button class="link-button" type="button" @click="resetOtp">
          {{ $t('account.auth.changeEmail') }}
        </button>
      </div>
    </form>

    <form v-else @submit.prevent="submitForm">
      <label>
        {{ isLogin ? $t('account.auth.usernameOrEmail') : $t('account.auth.yourEmail') }}
        <input type="text" v-model="username" />
      </label>
      <label>
        {{ $t('account.auth.password') }}
        <input type="password" v-model="password" />
      </label>
      <label v-if="!isLogin">
        {{ $t('account.auth.confirmPassword') }}
        <input type="password" v-model="confirmPassword" />
      </label>

      <label v-if="!isLogin"
        >{{ $t('account.auth.chooseNickname') }}
        <div class="nickname-input-container">
          <input type="text" v-model="nickname" />
          <button
            class="btn-round"
            type="button"
            :aria-label="$t('account.auth.randomNickname')"
            :title="$t('account.auth.randomNickname')"
            @click="nickname = generateNickname()"
          >
            🎲
          </button>
        </div>
      </label>

      <button class="btn-cta" type="submit">
        {{ isLogin ? $t('account.auth.login') : $t('account.auth.register') }}
      </button>
    </form>

    <p v-if="isLogin">
      <a href="#" @click.prevent="toggleMethod">
        {{ loginMethod === 'otp' ? $t('account.auth.usePassword') : $t('account.auth.useCode') }}
      </a>
    </p>

    <p>
      {{ isLogin ? $t('account.auth.needAccount') : $t('account.auth.haveAccount') }}
      <a href="#" @click="toggleForm">{{ isLogin ? $t('account.auth.register') : $t('account.auth.login') }}</a>
    </p>
  </div>
</template>

<script>
import $ from "jquery";
// const dotenv = require('dotenv');

export default {
  data() {
    return {
      isLogin: true,
      authProviders: [],
      username: "",
      nickname: "",
      password: "",
      confirmPassword: "",
      // Email code (OTP) is the default way to log in; password stays available.
      loginMethod: "otp",
      otpStep: "email",
      otpEmail: "",
      otpId: "",
      otpCode: "",
      authError: "",
      busy: false,
    };
  },
  created() {
    this.loadProviders();
  },
  methods: {
    async loadProviders() {
      const authMethods = await this.$pb.collection("users").listAuthMethods();
      const buttons = [];
      const redirectPath = "/profile";
      const redirectUrl = `${process.env.VUE_APP_URL}${redirectPath}`;

      for (const provider of authMethods.authProviders) {
        const $button = $(`<button class="btn-standard"></button>`);

        $button.attr("title", this.$t('account.auth.loginWith', { provider: provider.name })).click(function () {
          // Store provider's data on click for verification in the redirect page
          localStorage.setItem("provider", JSON.stringify(provider));
          const authUrl = provider.authUrl + redirectUrl;
          window.location.href = authUrl;
        });

        $button.html(
          `<i class="fab fa-${provider.name}"></i> ${this.$t('account.auth.loginWith', {
            provider: provider.name.charAt(0).toUpperCase() + provider.name.slice(1),
          })}`,
        );
        buttons.push($button);
      }

      $("#socialbuttons").html(
        buttons.length ? buttons : `<li>${this.$t('account.auth.noProviders')}</li>`,
      );
    },

    toggleForm() {
      this.isLogin = !this.isLogin;
      this.username = "";
      this.password = "";
      this.confirmPassword = "";
      this.resetOtp();
    },

    toggleMethod() {
      this.loginMethod = this.loginMethod === "otp" ? "password" : "otp";
      this.resetOtp();
    },

    resetOtp() {
      this.otpStep = "email";
      this.otpId = "";
      this.otpCode = "";
      this.authError = "";
    },

    // Step 1: PocketBase emails a one-time code. It answers with an otpId even when the
    // address has no account (enumeration protection), so the UI never reveals that.
    async sendOtp() {
      this.authError = "";
      this.busy = true;
      try {
        // First code request for this address: make sure an account exists, otherwise PocketBase sends no email.
        // (The server hook pocketbase/pb_hooks/otp_signup.pb.js does this too; a refused create here is not fatal.)
        if (this.otpStep === "email") {
          try { await this.ensureAccount(this.otpEmail.trim()); } catch (e) { console.warn("Account pre-create skipped:", e && e.status); }
        }
        const res = await this.$pb.send("/api/collections/users/request-otp", {
          method: "POST",
          body: { email: this.otpEmail.trim() },
        });
        this.otpId = res.otpId;
        this.otpStep = "code";
        this.otpCode = "";
      } catch (error) {
        console.error("OTP request failed:", error && error.status, error && error.response);
        this.authError = this.$t("account.auth.otpRequestFailed");
      } finally {
        this.busy = false;
      }
    },

    // Creates the account for a new email address with a random password the person never needs
    // (they sign in with codes). Entering the first code marks the email as verified in PocketBase.
    // An address that already has an account is simply skipped, so the screen looks the same for both.
    async ensureAccount(email) {
      const bytes = new Uint8Array(24);
      crypto.getRandomValues(bytes);
      const password = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await this.$pb.collection("users").create({
            username: this.generateNickname(),
            email,
            password,
            passwordConfirm: password,
            points: 100,
            user_challenges: ["6xu57yodnwkhrmd"],
          });
          return;
        } catch (error) {
          const fields = (error && error.response && error.response.data) || {};
          if (fields.email) return; // account exists already (or the address is invalid: the OTP request then fails below)
          if (!fields.username) throw error; // anything else is a real failure
          // nickname collided: try another one
        }
      }
    },

    // Step 2: exchange otpId + code for an auth token and keep it in the SDK's auth store,
    // which is where the rest of the app reads the logged-in user from.
    async verifyOtp() {
      this.authError = "";
      this.busy = true;
      try {
        const res = await this.$pb.send("/api/collections/users/auth-with-otp", {
          method: "POST",
          body: { otpId: this.otpId, password: this.otpCode },
        });
        this.$pb.authStore.save(res.token, res.record);
        window.location.reload();
      } catch (error) {
        console.error(error);
        this.authError = this.$t("account.auth.otpInvalid");
        this.busy = false;
      }
    },

    openUrlInNewTab(url) {
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      if (isMobile) {
        window.location.href = url;
      } else {
        window.open(url, "_blank", "");
      }
    },

    generateNickname() {
      const adjectives = [
        "spandex",
        "lycraclad",
        "urban",
        "clumsy",
        "rusty",
        "hipster",
        "tandem",
        "cruiser",
        "schmetterling",
        "flamingo",
        "eichhoernchen",
        "knallbonbon",
        "cargo",
        "lasten",
        "doctor",
        "suv",
        "funky",
        "radical",
        "rebel",
        "green",
        "blue",
        "red",
        "yellow",
      ];
      const nouns = [
        "peddler",
        "roadie",
        "hopper",
        "chaser",
        "crasher",
        "wheeler",
        "spinner",
        "glider",
        "pacer",
        "radler",
        "raser",
        "koenig",
        "pirat",
        "fahrer",
        "schrauber",
        "flitzer",
        "blitz",
        "schwalbe",
        "hauler",
        "schlepper",
        "dreamer",
        "rebel",
        "warrior",
        "activist",
        "cyclepreneur",
        "cargopreneur",
        "climatecourier",
        "lastenradler",
        "sprinter",
        "ecowarrior",
        "veloflaneur",
      ];
      const randomNumber = Math.floor(Math.random() * 10000);
      const adjective =
        adjectives[Math.floor(Math.random() * adjectives.length)];
      const noun = nouns[Math.floor(Math.random() * nouns.length)];
      const nickname = `${adjective}${noun}${randomNumber
        .toString()
        .padStart(4, "0")}`;
      return nickname.toLowerCase();
    },

    async submitForm() {
      const userCollection = this.$pb.collection("users");
      try {
        if (this.isLogin) {
          const authData = await userCollection.authWithPassword(
            this.username,
            this.password,
          );
          const userId = authData.model ? authData.model.id : null;
          this.$store.dispatch("loginUser", { token: authData.token, userId });
        } else {
          if (this.password !== this.confirmPassword) {
            throw new Error(this.$t('account.auth.passwordMismatch'));
          }

          const data = {
            username: this.nickname,
            email: this.username,
            password: this.password,
            passwordConfirm: this.confirmPassword,
            name: this.name,
            points: 100,
            user_challenges: ["6xu57yodnwkhrmd"],
          };

          const record = await userCollection.create(data);
          console.log(record);
          await this.$pb.collection("users").requestVerification(this.username);
          alert(
            this.$t('account.auth.registered', { email: this.username }),
          );
        }
        window.location.reload();
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style scoped>
.nickname-input-container {
  display: flex;
}

.social {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
}

form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.otp-hint {
  margin: 0;
}

.otp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
}

.auth-error {
  margin: 0;
  color: var(--color-error-dark);
}
.auth-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  margin: auto;
  margin-bottom: 64px;
}

.auth-form h2 {
  margin-top: 0;
  margin-bottom: 24px;
  font-size: 28px;
  font-weight: 300;
  text-align: center;
}

.auth-form label {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 300;
  text-align: left;
}

.auth-form input[type="text"],
.auth-form input[type="email"],
.auth-form input[type="password"] {
  padding: 12px;
  border: none;
  border-bottom: 1px solid var(--color-border);
  font-size: 18px;
}

.auth-form input[type="text"]:focus,
.auth-form input[type="email"]:focus,
.auth-form input[type="password"]:focus {
  outline: none;
  border-bottom: 1px solid var(--color-primary);
}
</style>
